import React, { type ReactNode } from 'react';
import { StyleSheet, View, type ViewStyle } from 'react-native';
import { spacing } from '../constants/spacing';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';

type Props = {
  children: ReactNode;
  style?: ViewStyle;
};

function flattenChildren(children: ReactNode): React.ReactNode[] {
  const items: React.ReactNode[] = [];
  React.Children.forEach(children, child => {
    if (child == null || child === false) return;
    if (React.isValidElement(child) && child.type === React.Fragment) {
      items.push(
        ...flattenChildren((child.props as { children?: ReactNode }).children),
      );
      return;
    }
    items.push(child);
  });
  return items;
}

/** Stacks on mobile/tablet; two equal panes from laptop width up. */
export function ResponsiveSplit({ children, style }: Props) {
  const { isWide } = useResponsiveLayout();
  const panes = flattenChildren(children);

  return (
    <View style={[styles.root, isWide ? styles.row : styles.column, style]}>
      {panes.map((child, index) => (
        <View
          key={index}
          style={[styles.pane, isWide ? styles.paneWide : styles.paneStacked]}
        >
          {child}
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  root: { width: '100%', minWidth: 0, gap: spacing.md },
  row: { flexDirection: 'row', alignItems: 'flex-start' },
  column: { flexDirection: 'column' },
  pane: { minWidth: 0 },
  paneStacked: { width: '100%' },
  paneWide: { flex: 1 },
});
